/**
 * Rows excluded from a run's reconciliation — never silently dropped.
 *
 * SPEC-0001 §Open questions #2 (resolved 2026-10-16): a run proceeds with
 * whatever rows validated, rather than blocking over a handful of bad ones.
 * That is only safe because every exclusion stays visible on the run record,
 * which is what this panel is for — shown above the findings, not folded
 * into a log no one reads, since a viewer needs to know what the numbers
 * below do *not* include before trusting them.
 */
import type { RunDetail } from '@picklii/shared';

export function RowExclusions({ rowErrors }: { rowErrors: RunDetail['rowErrors'] }) {
	if (rowErrors.length === 0) return null;

	return (
		<section className="card bg-base-200 border-base-300 col-span-12 border shadow-xs">
			<div className="card-body gap-4">
				<div>
					<h2 className="card-title text-base">
						{rowErrors.length} row{rowErrors.length === 1 ? '' : 's'} excluded from this run
					</h2>
					<p className="text-base-content/70 text-sm">
						These rows failed validation and are not reflected in the findings below. Fix them in
						your source file and re-upload if they should be included — nothing below was computed
						from them.
					</p>
				</div>
				<div className="overflow-x-auto">
					<table className="table table-sm">
						<thead>
							<tr>
								<th>File</th>
								<th>Line</th>
								<th>Column</th>
								<th>Problem</th>
							</tr>
						</thead>
						<tbody>
							{rowErrors.map((rowError, index) => (
								<tr key={index}>
									<td>
										{rowError.source === 'sold' ? 'Sold hours' : 'PSA budget'}
										{/* Only sold ever has more than one file, so this is the
										    only place the filename disambiguates anything. */}
										{rowError.file !== undefined && (
											<span className="text-base-content/70 block font-mono text-xs">
												{rowError.file}
											</span>
										)}
									</td>
									<td className="tabular-nums">{rowError.line}</td>
									<td>{rowError.column ?? '—'}</td>
									<td>{rowError.message}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</div>
		</section>
	);
}
